import { useSetAtom } from "jotai";
import { useEffect } from "react";
import Overview from "./components/Overview";
import { useIsMobile } from "./hooks/useIsMobile";
import { isMobileAtom, isShowDrawerInMobileAtom, tokenAtom, userIdAtom } from "./store";
import { globalEventEmitter } from "./utils/eventEmitter";

const Loader = () => {
  return (
    <div className="idto_digilocker_loader">
      <div className="idto_digilocker_loader_spinner" />
    </div>
  )
}

export default function App({ 
  userId, 
  isShowDrawerInMobile = false, 
  token,
  onSessionInitiated,
  onReferenceReceived,
  onSessionCompleted,
  onError,
  onSessionExpired,
  onLanguageChanged,
  onAudioToggled
}: { 
  userId: string, 
  isShowDrawerInMobile?: boolean, 
  token: string,
  onSessionInitiated?: (data: { user_id: string; redirect_url: string }) => void,
  onReferenceReceived?: (data: { user_id: string; reference_key: string; expires_at: string }) => void,
  onSessionCompleted?: (data: { user_id: string }) => void,
  onError?: (data: { user_id: string; error: string; error_code?: string }) => void,
  onSessionExpired?: (data: { user_id: string; reference_key: string }) => void,
  onLanguageChanged?: (data: { language: string; language_code: string }) => void,
  onAudioToggled?: (data: { is_muted: boolean }) => void
}) {
  const { isMobile, containerRef } = useIsMobile();
  const setUserId = useSetAtom(userIdAtom);
  const setIsShowDrawerInMobile = useSetAtom(isShowDrawerInMobileAtom);
  const setToken = useSetAtom(tokenAtom);
  const setIsMobile = useSetAtom(isMobileAtom);

  if (!userId || !token) {
    throw new Error('userId and token are required');
  }

  useEffect(() => {
    setUserId(userId);
    setIsShowDrawerInMobile(isShowDrawerInMobile);
    setToken(token);
    setIsMobile(isMobile);
  }, [userId, isShowDrawerInMobile, token, isMobile, setUserId, setIsShowDrawerInMobile, setToken, setIsMobile]);

  // Set up event listeners for the SDK consumer
  useEffect(() => {
    const cleanupFunctions: (() => void)[] = [];

    if (onSessionInitiated) {
      const handleSessionInitiated = (event: any) => {
        if (event.data && event.data.user_id === userId) {
          onSessionInitiated(event.data);
        }
      };
      globalEventEmitter.on('SESSION_INITIATED', handleSessionInitiated);
      cleanupFunctions.push(() => globalEventEmitter.off('SESSION_INITIATED', handleSessionInitiated));
    }

    if (onReferenceReceived) {
      const handleReferenceReceived = (event: any) => {
        if (event.data && event.data.user_id === userId) {
          onReferenceReceived(event.data);
        }
      };
      globalEventEmitter.on('REFERENCE_RECEIVED', handleReferenceReceived);
      cleanupFunctions.push(() => globalEventEmitter.off('REFERENCE_RECEIVED', handleReferenceReceived));
    }

    if (onSessionCompleted) {
      const handleSessionCompleted = (event: any) => {
        if (event.data && event.data.user_id === userId) {
          onSessionCompleted(event.data);
        }
      };
      globalEventEmitter.on('SESSION_COMPLETED', handleSessionCompleted);
      cleanupFunctions.push(() => globalEventEmitter.off('SESSION_COMPLETED', handleSessionCompleted));
    }

    if (onError) {
      const handleError = (event: any) => {
        if (event.data && event.data.user_id === userId) {
          onError(event.data);
        }
      };
      globalEventEmitter.on('ERROR_OCCURRED', handleError);
      cleanupFunctions.push(() => globalEventEmitter.off('ERROR_OCCURRED', handleError));
    }

    if (onSessionExpired) {
      const handleSessionExpired = (event: any) => {
        if (event.data && event.data.user_id === userId) {
          onSessionExpired(event.data);
        }
      };
      globalEventEmitter.on('SESSION_EXPIRED', handleSessionExpired);
      cleanupFunctions.push(() => globalEventEmitter.off('SESSION_EXPIRED', handleSessionExpired));
    }

    if (onLanguageChanged) {
      const handleLanguageChanged = (event: any) => {
        onLanguageChanged(event.data);
      };
      globalEventEmitter.on('LANGUAGE_CHANGED', handleLanguageChanged);
      cleanupFunctions.push(() => globalEventEmitter.off('LANGUAGE_CHANGED', handleLanguageChanged));
    }

    if (onAudioToggled) {
      const handleAudioToggled = (event: any) => {
        onAudioToggled(event.data);
      };
      globalEventEmitter.on('AUDIO_TOGGLED', handleAudioToggled);
      cleanupFunctions.push(() => globalEventEmitter.off('AUDIO_TOGGLED', handleAudioToggled));
    }

    // Cleanup function
    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [onSessionInitiated, onReferenceReceived, onSessionCompleted, onError, onSessionExpired, onLanguageChanged, onAudioToggled, userId]);

  const containerClassName = `idto_digilocker_app_container ${isMobile ? 'idto_digilocker_mobile_min_height' : 'idto_digilocker_desktop_min_height'}`;

  return (
    <div
      ref={containerRef}
      className={containerClassName}
    >
      {isMobile !== null && <Overview />}
      {isMobile == null && <Loader />}
    </div>
  )
}